import { get, isEqual } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import findResult, {
  findSelected,
  findWithCodes,
  hasKey,
  isEmpty,
  removeEmpty,
  selectableResultWithDates,
} from '@/utils/resultsHelper';
import { pruneEntityFieldErrors } from '@/utils/errors';
import { formatDate } from '@/utils/dateHelper';
import defaultStates, {
  saveStates,
  readStates,
  states,
  subscribeStates,
} from '@/constants/lifecycle';
import pagingWithFilteredResults, {
  presentationOptions,
  sortingFilters,
  statusFilters,
  visibilityFilters,
} from '@/constants/states';
import fieldNames, { columnName, orderByColumns } from '@/constants/fields';
import { newRequestUrl } from '@/constants/urls';
import { isCommonsModeEnabled, isPmnTransferEnabled } from '@/auth/featureToggles';
import { datasetSettingsFields } from '@/datasets/settings/state/state';
import { workflowSections } from '@/workflows/state/state';
import datasetStatusInitialState from '@/datasets/status/state/state';
import { ATTACHMENT_TYPE } from '@/datasets/attachments/constants';
import { workspaceForms } from '@/workspaces/constants';
import tasksInitialState, { requestTasksStateToSave, requestTaskStateToLoad } from '../tasks/state/state';
import {
  approvalStatus,
  requestCategories,
  requestSources,
  requestTransferTypes,
} from '../constants';
import { activeRequestFieldArray } from '../forms/state/state';

export const statusPanels = ({ status }) => {
  const openPanels = [1, 2];
  if (status === approvalStatus.DENY) {
    openPanels.unshift(0);
  }

  return ({
    panels: {
      single: openPanels,
      left: openPanels,
      middle: [0],
      right: [0],
    },
  });
};

export const requestPanels = (openPanels = [0]) => ({
  panels: {
    single: openPanels,
    left: openPanels,
    middle: openPanels,
    right: openPanels,
  },
});

const allRequests = (
  pendingFlag,
  approvedFlag,
  deniedFlag,
  errorFlag,
  display = presentationOptions.TABLE,
  orderBy = [{ column: 'updated_at', direction: 'desc' }],
  singleSorting = true,
) => ({
  all: {
    ...pagingWithFilteredResults(),
    ...statusFilters(pendingFlag, approvedFlag, deniedFlag, errorFlag),
    ...defaultStates,
    ...sortingFilters(orderBy, singleSorting),
    display,
    multiSelect: true,
    reviewFlag: false,
  },
});

const reviewRequests = (
  pendingFlag,
  approvedFlag,
  deniedFlag,
  display = presentationOptions.TABLE,
  orderBy = [{ column: 'updated_at', direction: 'desc' }],
  singleSorting = true,
) => ({
  review: {
    ...pagingWithFilteredResults(),
    ...statusFilters(pendingFlag, approvedFlag, deniedFlag, false),
    ...defaultStates,
    ...sortingFilters(orderBy, singleSorting),
    display,
    multiSelect: true,
    reviewFlag: true,
  },
});

export const openRequest = ({ code }, readStatus = states.PENDING) => ({
  code,
  isDirty: false,
  readStatus,
  readErrors: [],
  dictionaries: [],
  workflows: [{ code: null }],
  workspaceReadStatus: states.NONE,
  workspaceReadErrors: [],
  ...requestPanels(),
});

const requestSource = (privateFlag, internalFlag, publicFlag) => (
  {
    requestableFlag: true,
    ...pagingWithFilteredResults(),
    ...readStates,
    ...visibilityFilters(privateFlag, internalFlag, publicFlag),
    multiSelect: false,
  }
);

const requestWorkspacesState = () => ({ results: [], ...readStates, multiSelect: false });
const requestLocationsState = () => ({ results: [], ...readStates });

const requestSourceResult = ({
  code,
  namespaces,
  settings,
  status,
}) => ({
  catalogue: { title: '' },
  catalogueReadStatus: states.NONE,
  code,
  namespaces,
  ...readStates,
  settings,
  settingsReadStatus: states.NONE,
  status,
});

const datasetSourceResults = ({ steps: { datasets: { results } } }) => (
  findSelected(results).map((item) => ({ ...requestSourceResult(item), dictionaries: [] }))
);

const cohortSourceResults = ({ steps: { cohorts: { results } } }) => (
  findSelected(results).map((item) => ({ ...requestSourceResult(item), queries: [] }))
);

const newRequestSourceResult = (code) => requestSourceResult({
  code,
  settings: datasetSettingsFields(),
  status: datasetStatusInitialState(),
  namespaces: [],
});

const sourceAndStepAttributes = (code, requestSourceCode, requestSourceType) => {
  const activeStepIndex = requestSourceCode ? 1 : 0;
  return ({
    activeStepIndex,
    results: requestSourceCode ? [newRequestSourceResult(requestSourceCode)] : [],
    sourceUrl: requestSourceCode ? newRequestUrl(requestSourceType, requestSourceCode, code) : null,
  });
};

export const requestAttachmentsState = (results = []) => ({
  results,
  folderResults: [],
  navigation: { ...readStates, results: [] },
  ...readStates,
  multiSelect: true,
  selectedInitially: {},
  filters: {
    byType: {
      [ATTACHMENT_TYPE.attachment.name]: true,
      [ATTACHMENT_TYPE.datafile.name]: true,
      [ATTACHMENT_TYPE.folder.name]: true,
    },
    byTextInput: '',
  },
  folders: {
    currentPathSegments: [],
  },
  ...sortingFilters([{ column: orderByColumns[columnName], direction: 'asc' }]),
});

export const newRequest = ({ code, requestSourceCode, requestSourceType }) => ({
  ...openRequest({ code }, states.SUCCESS),
  ...saveStates,
  isNew: true,
  created_at: Date.now(),
  updated_at: Date.now(),
  name: '',
  destination_namespace: null,
  fields: {},
  request_source: requestSourceType,
  workspace_uuid: null,
  workspace_location: null,
  workspace_requested: false,
  transfer: { transfer_type: '' },
  steps: {
    attachments: requestAttachmentsState(),
    cohorts: requestSource(true, true, null),
    datasets: requestSource(true, true, isCommonsModeEnabled()),
    locations: requestLocationsState(),
    namespaceSelected: false,
    pollStatus: states.NONE,
    ...sourceAndStepAttributes(code, requestSourceCode, requestSourceType),
    subscriptionsReadStatus: states.NONE,
    subscriptionsReadErrors: [],
    ...subscribeStates,
    terms: false,
    workflowCategory: 'open',
    workspaces: requestWorkspacesState(),
  },
});

const initialState = {
  allowPmnTransfer: isPmnTransferEnabled(),
  approveRequestsOpen: false,
  archiveRequestOpen: false,
  deleteRequestOpen: false,
  newRequestCode: uuidv4(),
  requestFieldItems: activeRequestFieldArray(),
  requests: {
    ...allRequests(true, true, true, true),
    ...reviewRequests(true, false, false),
    open: { results: [] },
    approval: { results: [], action: approvalStatus.DENY },
    transfer: { results: [] },
    deletion: { results: [] },
    workspaces: { results: [] },
    tasks: { results: [] },
  },
  ...tasksInitialState,
  transferRequestsOpen: false,
};

export const requestsResult = (result) => ({
  ...selectableResultWithDates(result),
  transferredOn: result.transfer.transferred_at ? formatDate(result.transfer.transferred_at) : '',
});

const pruneRequestNamespaceErrors = (errors, { properties }) => {
  let filteredErrors = errors;
  if (hasKey(properties, 'destination_namespace')) {
    filteredErrors = pruneEntityFieldErrors(filteredErrors, fieldNames.NAMESPACE, ['workspace']);
  }

  if (hasKey(properties, 'steps')
      && hasKey(properties.steps, 'namespaceSelected')
      && properties.steps.namespaceSelected === false
  ) {
    filteredErrors = pruneEntityFieldErrors(filteredErrors, fieldNames.NAMESPACE, ['workspace']);
  }

  return filteredErrors;
};

export const pruneFieldErrors = (errors, { entity, properties }) => {
  let filteredErrors = errors;
  Object.keys(properties).forEach((key) => {
    filteredErrors = pruneEntityFieldErrors(filteredErrors, key, [entity]);
  });

  return filteredErrors;
};

export const pruneRequestErrors = (errors, { properties }) => {
  let filteredErrors = errors;
  if (hasKey(properties, 'workspace')) {
    filteredErrors = pruneEntityFieldErrors(filteredErrors, fieldNames.WORKSPACE, ['workspace']);
  }

  filteredErrors = pruneRequestNamespaceErrors(filteredErrors, { properties });

  return filteredErrors;
};

const requestSourceTypeHasChanged = (request) => {
  const [firstSource] = request.steps.results;
  return (firstSource ? (hasKey(firstSource, 'queries') && request.request_source === requestSources.DATASET)
      || (hasKey(firstSource, 'dictionaries') && request.request_source === requestSources.COHORT) : true);
};

export const selectRequestSources = (request) => {
  const workflows = request.steps.results.map(({ settings }) => settings.workflow_key).sort();
  const codes = request.steps.results.map(({ code }) => code).sort();
  const selected = request.request_source === requestSources.COHORT
    ? cohortSourceResults(request)
    : datasetSourceResults(request);
  const sourcesChanged = requestSourceTypeHasChanged(request)
      || (!isEqual(codes, selected.map(({ code }) => code).sort()));
  const fieldsChanged = !isEqual(workflows, selected.map(({ settings }) => settings.workflow_key).sort());
  const namespace = sourcesChanged ? null : request.destination_namespace;

  return {
    attachments: sourcesChanged ? requestAttachmentsState() : request.steps.attachments,
    fields: fieldsChanged ? {} : request.fields,
    locations: sourcesChanged ? requestLocationsState() : request.steps.locations,
    namespace: request.steps.namespaceSelected ? namespace : null,
    sources: sourcesChanged ? selected : request.steps.results,
    workspaces: sourcesChanged ? requestWorkspacesState() : request.steps.workspaces,
  };
};

export const requestStateToLoad = ({ requests }) => {
  const items = get(requests, 'requests', initialState.requests);
  const tasks = get(requests, 'tasks', initialState.tasks);
  const allFilters = items.all || initialState.requests.all;
  const reviewFilters = items.review || initialState.requests.review;

  return {
    ...initialState,
    requests: {
      ...initialState.requests,
      ...allRequests(
        allFilters.pendingFlag,
        allFilters.approvedFlag,
        allFilters.deniedFlag,
        allFilters.errorFlag,
        allFilters.display,
        allFilters.orderBy,
        allFilters.singleSorting,
      ),
      ...reviewRequests(
        reviewFilters.pendingFlag,
        reviewFilters.approvedFlag,
        reviewFilters.deniedFlag,
        reviewFilters.display,
        reviewFilters.orderBy,
        reviewFilters.singleSorting,
      ),
    },
    ...requestTaskStateToLoad(tasks),
  };
};

const flagsToSave = ({
  approvedFlag,
  deniedFlag,
  display,
  errorFlag,
  orderBy,
  pendingFlag,
  singleSorting,
}) => ({
  approvedFlag,
  deniedFlag,
  display,
  errorFlag,
  orderBy,
  pendingFlag,
  singleSorting,
});

export const requestStateToSave = ({ requests }) => (
  {
    requests: {
      requests: {
        all: flagsToSave(requests.requests.all),
        review: flagsToSave(requests.requests.review),
      },
      ...requestTasksStateToSave(requests),
    },
  }
);

const requestWorkflowCode = ({ workflows }, { source }) => (source ? source.settings.workflow_key : workflows[0].code);

const requestWorkflow = ({ isNew, steps }, workflows, { workflowKey }) => (
  findResult(workflows, isNew ? steps.workflowCategory : 'open', workflowKey)
  || { ...readStates, ...workflowSections() }
);

export const requestReferences = (request, workflows, requestIfNoExists = undefined) => {
  const attributes = {
    source: null, workflowKey: null, workflow: null, exists: !isEmpty(request), request,
  };

  if (attributes.exists) {
    attributes.source = request.isNew ? request.steps.results[0] : null;
    attributes.workflowKey = requestWorkflowCode(request, attributes);
    attributes.workflow = requestWorkflow(request, workflows, attributes);
  } else {
    attributes.request = requestIfNoExists;
  }

  return attributes;
};

export const resolveRequestWorkflow = (requests, workflows, { category, code }, requestIfNoExists = undefined) => {
  const request = findResult(requests, category, code) || findResult(requests, category, code, 'previousCode');
  return requestReferences(request, workflows, requestIfNoExists);
};

export const resolveRequestForm = (requests, workflows, forms, { category, code }, requestIfNoExists = undefined) => {
  const { request, workflow } = resolveRequestWorkflow(requests, workflows, { category, code }, requestIfNoExists);
  const attributes = { request, sections: workflow?.sections || {} };

  if (request?.workspace_requested || request?.steps?.isWorkspace) {
    const form = findResult(forms, 'open', workspaceForms.REQUEST);
    if (form) {
      attributes.sections = { ...attributes.sections, ...form.sections };
    }
  }

  return attributes;
};

export const supportedRequestTransferTypes = ({ settings }, allowPmnTransfer) => {
  const transferTypes = { ...requestTransferTypes };
  if (!settings.allow_manual) { delete transferTypes.MANUAL; }
  if (!settings.allow_clear) { delete transferTypes.CLEAR; }
  if (!allowPmnTransfer || !settings.allow_pseudonymised) { delete transferTypes.PMN; }
  if (!settings.allow_token) { delete transferTypes.TOKEN; }

  return transferTypes;
};

export const safeTransferType = ({ transfer }, { settings }, allowPmnTransfer) => {
  const transferTypes = Object.values(supportedRequestTransferTypes({ settings }, allowPmnTransfer));

  if ((!transfer.transfer_type && transferTypes.length > 1) || transferTypes.includes(transfer.transfer_type)) {
    return transfer.transfer_type;
  }
  return transferTypes[0];
};

export const requestWorkspaceAttributes = (request) => {
  const attributes = { workspace_requested: request.workspace_requested };
  if (!attributes.workspace_requested) {
    attributes.workspace_uuid = request.workspace_uuid;
    attributes.workspace_location = request.workspace_location;
  }

  return attributes;
};

export const findRequests = ({ requests }, { category }) => requests[category];

export const findAllRequests = ({ requests }, { items }) => {
  const codes = items.map(({ code }) => code);
  return findWithCodes(requests, requestCategories, codes);
};

export const findAllRequestsInCategories = ({ requests }, { code }) => {
  const categories = findAllRequests({ requests }, { items: [{ code }] });
  return removeEmpty(categories.map((category) => findResult(requests, category, code)));
};

export const findRequest = ({ requests }, { category, code }) => findResult(requests, category, code);

export const findRequestSource = ({ requests }, { category, code }) => {
  const item = findRequest({ requests }, { category, code });
  const { results } = item.steps;
  const [source] = results;

  return { item, results, source };
};

export const resetWorkspaceAttributes = () => ({
  workspace_reference: null,
  workspace_request_form: null,
  workspace_name: null,
  workspace_location: null,
});

export const requestDatasetCode = (request, source) => (
  {
    [requestSources.COHORT]: get(source, 'queries[0].dataset.code'),
    [requestSources.DATASET]: source.code,
  }[request.request_source]
);

export const requestActionsState = ({ category, code }, requests, workflows, locations, forms) => {
  const { request, source, workflow } = resolveRequestWorkflow(requests, workflows, {
    category,
    code,
  }, newRequest({ category, code }));
  const { steps } = request;
  const sources = request.request_source === requestSources.COHORT ? steps.cohorts : steps.datasets;
  const safeSources = sources || { ...readStates, results: [] };
  const requested = source || { ...readStates, settings: datasetSettingsFields(), settingsReadStatus: states.NONE };
  const form = findResult(forms, 'open', workspaceForms.REQUEST);

  return {
    form,
    readErrors: request.readErrors.concat(safeSources.readErrors).concat(locations.lookups.readErrors)
      .concat(workflow?.readErrors ?? []).concat(requested.readErrors)
      .concat(steps.workspaces.readErrors)
      .concat(requested.readErrors)
      .concat(form.readErrors),
    request,
    requested,
    saveStatus: request.saveStatus,
    sources: safeSources,
    steps,
    workflow,
  };
};

export default initialState;
